// Platform settings: the MFA policy (decision B2: require an authenticator
// app per layer), the partner network policy (Phase 8: live partner calls only
// from Kenyan addresses and approved networks) and the platform admin team
// (up to five). Every change is confirmed with a code; one that weakens a
// protection, and any change to the team, waits for a second administrator
// (ADR 0041).
import {
  Badge,
  Button,
  Group,
  Loader,
  NativeSelect,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { useStepUp } from "../../shared/session/stepup";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog";
import { AdminLayout } from "./AdminLayout";
import { Requested, useRequested } from "./approvals";

const KEY = ["admin", "settings", "mfa"] as const;

type Setting = "totp_required_api" | "totp_required_integrations";

export function AdminSettingsPage() {
  const { t } = useTranslation();
  return (
    <AdminLayout title={t("admin.title")}>
      <Stack gap="lg">
        <MFASettings />
        <PartnerAccess />
        <AdminTeam />
      </Stack>
    </AdminLayout>
  );
}

function MFASettings() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const requested = useRequested();
  const settings = useQuery({ queryKey: KEY, queryFn: () => unwrap(api.GET("/admin/settings/mfa")) });
  const [saving, setSaving] = useState<Setting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<"saved" | "requested" | null>(null);
  const s = settings.data;

  const toggle = async (key: Setting, value: boolean) => {
    if (!s) return;
    setError(null);
    setSaved(null);
    const token = await stepUp("admin_change");
    if (!token) return;
    setSaving(key);
    const body = {
      totp_required_api: s.totp_required_api,
      totp_required_integrations: s.totp_required_integrations,
      [key]: value,
    };
    const { data, error, response } = await api.PUT("/admin/settings/mfa", {
      params: { header: { "X-Step-Up-Token": token } },
      body,
    });
    setSaving(null);
    if (response.status === 202) {
      requested();
      return setSaved("requested");
    }
    if (!data) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData<Schemas["MFASettings"]>(KEY, data as Schemas["MFASettings"]);
    void queryClient.invalidateQueries({ queryKey: ["me"] });
    setSaved("saved");
  };

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Title order={2} size="h4">
          {t("admin_settings.title")}
        </Title>
        <Text size="sm">{t("admin_settings.intro")}</Text>
        {settings.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : !s ? (
          <FormError message={errorMessage(settings.error, t("error.generic"))} />
        ) : (
          <>
            <Switch
              label={t("admin_settings.api")}
              description={t("admin_settings.api_hint")}
              checked={s.totp_required_api}
              disabled={saving !== null}
              onChange={(e) => void toggle("totp_required_api", e.currentTarget.checked)}
            />
            <Switch
              label={t("admin_settings.integrations")}
              description={t("admin_settings.integrations_hint")}
              checked={s.totp_required_integrations}
              disabled={saving !== null}
              onChange={(e) => void toggle("totp_required_integrations", e.currentTarget.checked)}
            />
            <SmsLimit settings={s} onSaved={setSaved} onError={setError} />
            {s.updated_at && (
              <Text size="xs" c="dimmed">
                {t("admin_settings.updated", { date: f.dateTime(s.updated_at) })}
              </Text>
            )}
          </>
        )}
        {saved === "saved" && <Notice>{t("admin_settings.saved")}</Notice>}
        {saved === "requested" && <Requested />}
        <FormError message={error} />
      </Stack>
    </Paper>
  );
}

/**
 * SmsLimit is how many sign-ins with an SMS code an account has before it must
 * set up an authenticator app (ADR 0039).
 */
function SmsLimit({
  settings,
  onSaved,
  onError,
}: {
  settings: Schemas["MFASettings"];
  onSaved: (how: "saved" | "requested") => void;
  onError: (msg: string | null) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const requested = useRequested();
  const [on, setOn] = useState(settings.sms_limit_on);
  const [count, setCount] = useState<number>(settings.sms_sign_ins_before_totp);
  const [busy, setBusy] = useState(false);
  const changed = on !== settings.sms_limit_on || count !== settings.sms_sign_ins_before_totp;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    onError(null);
    const token = await stepUp("admin_change");
    if (!token) return;
    setBusy(true);
    const { data, error, response } = await api.PUT("/admin/settings/mfa", {
      params: { header: { "X-Step-Up-Token": token } },
      body: {
        totp_required_api: settings.totp_required_api,
        totp_required_integrations: settings.totp_required_integrations,
        sms_limit_on: on,
        sms_sign_ins_before_totp: count,
      },
    });
    setBusy(false);
    if (response.status === 202) {
      requested();
      return onSaved("requested");
    }
    if (!data) return onError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData<Schemas["MFASettings"]>(KEY, data as Schemas["MFASettings"]);
    void queryClient.invalidateQueries({ queryKey: ["me"] });
    onSaved("saved");
  };

  return (
    <form onSubmit={save} data-testid="sms-limit">
      <Stack gap="xs">
        <Switch
          label={t("admin_settings.sms_limit")}
          description={t("admin_settings.sms_limit_hint")}
          checked={on}
          onChange={(e) => setOn(e.currentTarget.checked)}
        />
        <Group align="flex-end" gap="xs">
          <NumberInput
            label={t("admin_settings.sms_count")}
            description={t("admin_settings.sms_count_hint")}
            min={0}
            max={50}
            allowDecimal={false}
            allowNegative={false}
            clampBehavior="strict"
            value={count}
            disabled={!on}
            onChange={(v) => setCount(typeof v === "number" ? v : Number(v) || 0)}
            w={220}
          />
          <Button type="submit" loading={busy} disabled={!changed}>
            {t("admin_settings.sms_save")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

const ACCESS_KEY = ["admin", "settings", "partner-access"] as const;

type Policy = Schemas["PartnerAccessPolicy"];
type PolicySwitch =
  | "require_kenya_ip"
  | "require_trusted_network"
  | "require_live_approval"
  | "require_registered_purpose"
  | "auto_suspend";

// Approval windows offered to admins, in minutes (the API takes 10 minutes to 7 days).
const WINDOWS = [60, 240, 720, 1440, 2880, 4320, 10080] as const;

function PartnerAccess() {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const requested = useRequested();
  const policy = useQuery({ queryKey: ACCESS_KEY, queryFn: () => unwrap(api.GET("/admin/settings/partner-access")) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<"saved" | "requested" | null>(null);
  const p = policy.data;

  const save = async (patch: Partial<Policy>) => {
    if (!p) return;
    setError(null);
    setSaved(null);
    const token = await stepUp("admin_change");
    if (!token) return;
    setSaving(true);
    const body = {
      require_kenya_ip: p.require_kenya_ip,
      require_trusted_network: p.require_trusted_network,
      require_live_approval: p.require_live_approval,
      approval_window_minutes: p.approval_window_minutes,
      require_registered_purpose: p.require_registered_purpose,
      requests_per_hour: p.requests_per_hour,
      same_code_per_day: p.same_code_per_day,
      auto_suspend: p.auto_suspend,
      ...patch,
    };
    const { data, error, response } = await api.PUT("/admin/settings/partner-access", {
      params: { header: { "X-Step-Up-Token": token } },
      body,
    });
    setSaving(false);
    if (response.status === 202) {
      requested();
      return setSaved("requested");
    }
    if (!data) return setError(errorMessage(error, t("error.generic")));
    queryClient.setQueryData<Policy>(ACCESS_KEY, data as Policy);
    setSaved("saved");
  };
  const toggle = (key: PolicySwitch) => (e: React.ChangeEvent<HTMLInputElement>) =>
    void save({ [key]: e.currentTarget.checked });
  const windowLabel = (m: number) =>
    m < 1440 ? t("partner_access.hours", { count: m / 60 }) : t("partner_access.days", { count: m / 1440 });

  return (
    <Paper withBorder p="md" radius="md" data-testid="partner-access">
      <Stack gap="md">
        <Title order={2} size="h4">
          {t("partner_access.title")}
        </Title>
        <Text size="sm">{t("partner_access.intro")}</Text>
        {policy.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : !p ? (
          <FormError message={errorMessage(policy.error, t("error.generic"))} />
        ) : (
          <>
            <Title order={3} size="h5">
              {t("partner_access.networks_title")}
            </Title>
            <Switch
              label={t("partner_access.kenya")}
              description={t("partner_access.kenya_hint")}
              checked={p.require_kenya_ip}
              disabled={saving}
              onChange={toggle("require_kenya_ip")}
            />
            <Switch
              label={t("partner_access.trusted")}
              description={t("partner_access.trusted_hint")}
              checked={p.require_trusted_network}
              disabled={saving}
              onChange={toggle("require_trusted_network")}
            />
            {!p.require_kenya_ip && !p.require_trusted_network && (
              <Text size="sm" c="orange.8" data-testid="partner-access-open">
                {t("partner_access.open_warning")}
              </Text>
            )}
            <Text size="xs" c="dimmed" data-testid="kenya-source">
              {t("partner_access.source", { source: p.kenya_ranges_source })}
            </Text>

            <Title order={3} size="h5">
              {t("partner_access.lookups_title")}
            </Title>
            <Switch
              label={t("partner_access.live")}
              description={t("partner_access.live_hint")}
              checked={p.require_live_approval}
              disabled={saving}
              onChange={toggle("require_live_approval")}
            />
            <NativeSelect
              label={t("partner_access.window")}
              description={t("partner_access.window_hint")}
              maw={320}
              value={String(p.approval_window_minutes)}
              disabled={saving || !p.require_live_approval}
              onChange={(e) => void save({ approval_window_minutes: Number(e.currentTarget.value) })}
              data={[...new Set([...WINDOWS, p.approval_window_minutes])]
                .sort((a, b) => a - b)
                .map((m) => ({ value: String(m), label: windowLabel(m) }))}
            />
            <Switch
              label={t("partner_access.purposes")}
              description={t("partner_access.purposes_hint")}
              checked={p.require_registered_purpose}
              disabled={saving}
              onChange={toggle("require_registered_purpose")}
            />

            <Title order={3} size="h5">
              {t("partner_access.limits_title")}
            </Title>
            <LimitsForm policy={p} saving={saving} onSave={(patch) => void save(patch)} />
            <Switch
              label={t("partner_access.auto_suspend")}
              description={t("partner_access.auto_suspend_hint")}
              checked={p.auto_suspend}
              disabled={saving}
              onChange={toggle("auto_suspend")}
            />
            {p.updated_at && (
              <Text size="xs" c="dimmed">
                {t("admin_settings.updated", { date: f.dateTime(p.updated_at) })}
              </Text>
            )}
          </>
        )}
        {saved === "saved" && <Notice>{t("admin_settings.saved")}</Notice>}
        {saved === "requested" && <Requested />}
        <FormError message={error} />
      </Stack>
    </Paper>
  );
}

function LimitsForm({
  policy: p,
  saving,
  onSave,
}: {
  policy: Policy;
  saving: boolean;
  onSave: (patch: Partial<Policy>) => void;
}) {
  const { t } = useTranslation();
  const [perHour, setPerHour] = useState<number | string>(p.requests_per_hour);
  const [perDay, setPerDay] = useState<number | string>(p.same_code_per_day);
  const valid = Number(perHour) >= 1 && Number(perDay) >= 1;
  const changed = Number(perHour) !== p.requests_per_hour || Number(perDay) !== p.same_code_per_day;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSave({ requests_per_hour: Number(perHour), same_code_per_day: Number(perDay) });
      }}
    >
      <Group align="flex-end" gap="sm" wrap="wrap">
        <NumberInput
          label={t("partner_access.requests_per_hour")}
          description={t("partner_access.requests_per_hour_hint")}
          value={perHour}
          onChange={setPerHour}
          min={1}
          max={1_000_000}
          allowDecimal={false}
          allowNegative={false}
          thousandSeparator=","
          w={220}
        />
        <NumberInput
          label={t("partner_access.same_code_per_day")}
          description={t("partner_access.same_code_per_day_hint")}
          value={perDay}
          onChange={setPerDay}
          min={1}
          max={10_000}
          allowDecimal={false}
          allowNegative={false}
          w={220}
        />
        <Button type="submit" variant="light" loading={saving} disabled={!valid || !changed}>
          {t("partner_access.save_limits")}
        </Button>
      </Group>
    </form>
  );
}

type Admin = Schemas["AccountSummary"];
const TEAM_KEY = ["admin", "admins"] as const;

function AdminTeam() {
  const { t } = useTranslation();
  const f = useFormat();
  const me = useMe();
  const stepUp = useStepUp();
  const requested = useRequested();
  const team = useQuery({ queryKey: TEAM_KEY, queryFn: () => unwrap(api.GET("/admin/admins")) });
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Admin | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<boolean>(false);
  const items = team.data?.items ?? [];
  const max = team.data?.max ?? 5;
  const full = items.length >= max;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(false);
    if (!/^[^\s@]+@[^\s@]+$/.test(email.trim())) return setError(t("admin_team.email_invalid"));
    const token = await stepUp("admin_change");
    if (!token) return;
    setAdding(true);
    const { error, response } = await api.POST("/admin/admins", {
      params: { header: { "X-Step-Up-Token": token } },
      body: { email: email.trim() },
    });
    setAdding(false);
    if (response.status !== 202) return setError(errorMessage(error, t("error.generic")));
    setEmail("");
    setNotice(true);
    requested();
  };

  const remove = async () => {
    if (!removing) return;
    setRemoveError(null);
    const token = await stepUp("admin_change");
    if (!token) return;
    setBusy(true);
    const { error, response } = await api.DELETE("/admin/admins/{account_id}", {
      params: { path: { account_id: removing.account_id }, header: { "X-Step-Up-Token": token } },
    });
    setBusy(false);
    if (response.status !== 202) return setRemoveError(errorMessage(error, t("error.generic")));
    setNotice(true);
    setRemoving(null);
    requested();
  };

  return (
    <Paper withBorder p="md" radius="md" data-testid="admin-team">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2} size="h4">
            {t("admin_team.title")}
          </Title>
          <Badge {...(full ? { color: "orange" } : { variant: "default" })} data-testid="admin-count">
            {t("admin_team.count", { count: items.length, max })}
          </Badge>
        </Group>
        <Text size="sm">{t("admin_team.intro")}</Text>
        {notice && <Requested />}
        {team.isPending ? (
          <Loader size="sm" aria-label={t("common.loading")} />
        ) : team.isError ? (
          <FormError message={errorMessage(team.error, t("error.generic"))} />
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing="sm" aria-label={t("admin_team.title")}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("staff.email")}</Table.Th>
                  <Table.Th>{t("admin_team.phone")}</Table.Th>
                  <Table.Th>{t("admin_team.last_sign_in")}</Table.Th>
                  <Table.Th>{t("staff.actions")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((a) => {
                  const self = a.account_id === me.data?.account_id;
                  return (
                    <Table.Tr key={a.account_id} data-testid="admin-row">
                      <Table.Td>
                        <Group gap="xs">
                          <Text size="sm">{a.masked_email}</Text>
                          {self && (
                            <Badge size="sm" variant="light">
                              {t("staff.you")}
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>{a.masked_phone}</Table.Td>
                      <Table.Td>{a.last_login_at ? f.dateTime(a.last_login_at) : t("admin_team.never")}</Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          disabled={items.length <= 1}
                          onClick={() => {
                            setRemoveError(null);
                            setRemoving(a);
                          }}
                          aria-label={t("admin_team.remove_for", { email: a.masked_email })}
                        >
                          {t("staff.remove")}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        <form onSubmit={(e) => void add(e)} noValidate>
          <Group align="flex-end" gap="sm" wrap="wrap">
            <TextInput
              label={t("admin_team.add_label")}
              description={full ? t("admin_team.full") : t("admin_team.add_hint")}
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={full}
              miw={280}
              required
            />
            <Button type="submit" loading={adding} disabled={full}>
              {t("admin_team.add")}
            </Button>
          </Group>
        </form>
        <FormError message={error} />
      </Stack>
      <ConfirmDialog
        opened={removing !== null}
        title={t("admin_team.remove_title")}
        confirmLabel={t("staff.remove")}
        busy={busy}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      >
        {removing?.account_id === me.data?.account_id
          ? t("admin_team.remove_self_body")
          : t("admin_team.remove_body", { email: removing?.masked_email ?? "" })}
      </ConfirmDialog>
    </Paper>
  );
}
